import {
  toWSDateString,
  WSFilter,
  WSFilterOption
} from "@wingspanhq/fe-component-library";
import {
  BulkStatus,
  IBulkCollaboratorBatch,
  ICustomField,
  IRequirementDefinitionResponse,
  PaymentRequirementStrategy,
  RequirementExpirationStrategy,
  RequirementReviewStrategy,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import { Engagement } from "@wingspanhq/wingspan-api-sdk";
import { INewUser } from "@wingspanhq/users/dist/lib/interfaces";
import { sortBy } from "lodash";
import { getUserName } from "../../../../../query/users/selectors";
import { PayerPayeeSearchableTinStatus } from "../../../../../services/search";
import { BulkResource } from "../../../../BulkImporter/types";
import { getUploadedFilename } from "../../../../BulkImporter/utils/getUploadedFilename";
import { getRequirementDefinitionName } from "../../../../RequirementDefinitions/utils";
import { customFieldsToFilters } from "../../../utils";
import {
  PayeeFilterEligibilityStatus,
  PayeeFilterRequirementStatus,
  PayeeFilters,
  PayeeFilterShareTaxDocument
} from "./types";

type Data = {
  orgAccounts: INewUser[];
  engagements: Engagement[];
  bulkBatches: IBulkCollaboratorBatch[];
  customFields: ICustomField[];
  requirements: IRequirementDefinitionResponse[];
};

function getRequirementStatusOptions(
  requirement: IRequirementDefinitionResponse
) {
  const hasReviewStrategy =
    requirement.reviewStrategy === RequirementReviewStrategy.AlwaysRequired ||
    requirement.reviewStrategy === RequirementReviewStrategy.OnFail;

  const hasExpirationStrategy =
    requirement.expirationStrategy ===
    RequirementExpirationStrategy.DaysAfterComplete;

  const options: WSFilterOption<"radio">[] = [
    {
      name: PayeeFilterRequirementStatus.All,
      label: "All",
      helperText: "All contractors assigned this requirement",
      hidePill: true
    }
  ];

  switch (requirement.requirementType) {
    case RequirementType.Registration:
      options.push({
        name: PayeeFilterRequirementStatus.Incomplete,
        label: "Awaiting contractor's sign up",
        group: "Incomplete"
      });
      break;

    case RequirementType.TaxVerification:
      options.push({
        name: PayeeFilterRequirementStatus.Incomplete,
        label: "Awaiting contractor's verification",
        group: "Incomplete"
      });
      break;

    case RequirementType.PayoutMethod:
      options.push({
        name: PayeeFilterRequirementStatus.Incomplete,
        label: "Awaiting contractor's payout method",
        group: "Incomplete"
      });
      break;

    case RequirementType.Signature:
      options.push({
        name: PayeeFilterRequirementStatus.AwaitingContractorSignature,
        label: "Awaiting contractor signature",
        group: "Incomplete"
      });

      options.push({
        name: PayeeFilterRequirementStatus.AwaitingClientSignature,
        label: "Awaiting your signature",
        helperText: "Only applicable to countersignature required",
        group: "Incomplete"
      });

      break;
    case RequirementType.SharedFile:
      options.push({
        name: PayeeFilterRequirementStatus.Incomplete,
        label: "Awaiting contractor upload",
        group: "Incomplete"
      });
      break;

    case RequirementType.IdentityVerification:
      options.push({
        name: PayeeFilterRequirementStatus.Incomplete,
        label: "Awaiting contractor completion",
        group: "Incomplete"
      });
      break;
    case RequirementType.BackgroundCheck:
      options.push({
        name: PayeeFilterRequirementStatus.PendingBackgroundCheck,
        label: "Awaiting background check completion",
        group: "Incomplete"
      });
      options.push({
        name: PayeeFilterRequirementStatus.ProcessingBackgroundCheck,
        label: "Processing",
        helperText: "Contractor submission is awaiting results",
        group: "Incomplete"
      });
      options.push({
        name: PayeeFilterRequirementStatus.FailedBackgroundCheck,
        label: "Failed background check",
        helperText: "Contractor submission failed and requires your attention",
        group: "Incomplete"
      });
      break;
    case RequirementType.InsuranceCoverage:
      options.push({
        name: PayeeFilterRequirementStatus.Incomplete,
        label: "Awaiting insurance coverage upload",
        group: "Incomplete"
      });
      break;
  }

  if (hasReviewStrategy) {
    options.push({
      name: PayeeFilterRequirementStatus.PendingApproval,
      label: "Awaiting your approval",
      helperText: "Contractor submission is awaiting your approval",
      group: "Incomplete"
    });

    options.push({
      name: PayeeFilterRequirementStatus.Rejected,
      label: "Rejected",
      helperText:
        "Contractor submission was rejected or marked incomplete by you",
      group: "Incomplete"
    });
  }

  if (hasExpirationStrategy) {
    options.push({
      name: PayeeFilterRequirementStatus.Expired,
      label: "Expired",
      helperText: "Contractor requirement has expired",
      group: "Incomplete"
    });
  }

  options.push({
    name: PayeeFilterRequirementStatus.Complete,
    label: "Complete",
    helperText: "Contractors with this requirement complete",
    group: "Complete"
  });

  if (hasReviewStrategy) {
    options.push({
      name: PayeeFilterRequirementStatus.Approved,
      label: "Approved",
      helperText:
        "Contractor submission was approved or marked complete by you",
      group: "Complete"
    });
  }

  return options;
}

function getVersionOptions(requirement: IRequirementDefinitionResponse) {
  const versionHistory: Array<{ version: number; name?: string }> =
    requirement.versionHistory ?? [];
  const currentVersion = requirement.version;

  const currentOption = {
    name: String(currentVersion),
    label: `v${currentVersion}`,
    helperText: `Current version`
  };

  const historyOptions = versionHistory
    .filter(v => v.version !== currentVersion)
    .sort((a, b) => b.version - a.version)
    .map(v => ({
      name: String(v.version),
      label: `v${v.version}`
    }));

  return [currentOption, ...historyOptions];
}

function getRequirementSubFilters(
  requirements: IRequirementDefinitionResponse[],
  requirementDefinitionId?: string
) {
  const requirementOptions = requirements.map(requirement => ({
    name: requirement.requirementDefinitionId,
    label: requirement.name,
    helperText: getRequirementDefinitionName({
      type: requirement.requirementType,
      // TODO(ivanh): add context and policy type here
      ...(requirement as any).dataSourceMetadata
    })
  }));

  const filters: WSFilter[] = [
    {
      title: "Requirement",
      titleInLabel: true,
      type: "select",
      name: "requirementDefinitionId",
      options: requirementOptions
    }
  ];

  const requirement = requirements.find(
    requirement =>
      requirement.requirementDefinitionId === requirementDefinitionId
  );

  if (requirement) {
    const versionOptions = getVersionOptions(requirement);

    if (versionOptions.length > 1) {
      filters.push({
        title: "Requirement version",
        type: "select",
        name: "requirementDefinitionVersion",
        options: versionOptions
      });
    }

    filters.push({
      title: "Requirement status",
      type: "radio",
      name: "requirementStatus",
      options: getRequirementStatusOptions(requirement)
    });
  }

  return filters;
}

export function buildConfig({
  orgAccounts,
  engagements,
  bulkBatches,
  customFields,
  requirements
}: Data) {
  return function (values: Partial<PayeeFilters>) {
    const config: WSFilter[] = [];

    if (orgAccounts.length > 0) {
      config.push({
        title: "Organizations",
        name: "payerId",
        type: "checkbox",
        titleInLabel: true,
        options: sortBy(orgAccounts, getUserName)
          .map(orgAccount => ({
            label: getUserName(orgAccount),
            name: orgAccount.userId
          }))
          .filter(o => o.label)
      });
    }

    if (engagements.length > 0) {
      const engagementOptions = engagements.map(engagement => ({
        label: engagement.name ?? "",
        name: engagement.id ?? ""
      }));

      config.push({
        title: "Engagement",
        name: "engagementId",
        type: "select",
        options: engagementOptions
      });
    }

    if (requirements.length > 0) {
      config.push({
        title: "Requirement status",
        type: "radio",
        name: "eligibilityStatus",
        options: [
          {
            name: PayeeFilterEligibilityStatus.All,
            label: "All requirements",
            helperText: "Contractors with assigned requirements",
            hidePill: true
          },
          {
            name: PayeeFilterEligibilityStatus.Complete,
            label: "All requirements complete",
            helperText: "Contractors with all requirements complete"
          },
          {
            name: PayeeFilterEligibilityStatus.Incomplete,
            label: "Has incomplete requirements",
            helperText: "Contractors with any requirements incomplete"
          },
          {
            name: PayeeFilterEligibilityStatus.SpecificRequirement,
            label: "Filter by specific requirement",
            helperText:
              "Contractors with specific requirement complete or incomplete",
            hidePill: true,
            subFilters: getRequirementSubFilters(
              requirements,
              values.requirementDefinitionId
            )
          }
        ]
      });
    }

    config.push({
      title: "Tax ID status",
      name: "searchableTinStatus",
      type: "checkbox",
      titleInLabel: true,
      options: [
        {
          label: "Not provided",
          helperText: "Tax ID has not been provided",
          name: PayerPayeeSearchableTinStatus.Unverified
        },
        {
          label: "Mismatch",
          helperText: "Tax ID and name is mismatched",
          name: PayerPayeeSearchableTinStatus.Failed
        },
        {
          label: "Pending",
          helperText: "Tax ID is being verified",
          name: PayerPayeeSearchableTinStatus.Pending
        },
        {
          label: "Verified",
          helperText: "Tax ID has been verified",
          name: PayerPayeeSearchableTinStatus.Verified
        }
      ]
    });

    config.push({
      title: "Tax info",
      name: "shareTaxDocument",
      type: "checkbox",
      titleInLabel: true,
      options: [
        {
          label: "Not shared",
          name: PayeeFilterShareTaxDocument.NotShared
        },
        {
          label: "Shared",
          name: PayeeFilterShareTaxDocument.Shared
        }
      ]
    });

    config.push({
      title: "Autopay",
      name: "autoPayStrategy",
      type: "checkbox",
      titleInLabel: true,
      options: [
        {
          label: "On",
          name: PaymentRequirementStrategy.All
        },
        {
          label: "Off",
          name: PaymentRequirementStrategy.None
        }
      ]
    });

    if (customFields.length > 0) {
      config.push(...customFieldsToFilters(customFields));
    }

    if (bulkBatches.length > 0) {
      const bulkBatchOptions = bulkBatches
        .filter(bulkBatch =>
          [BulkStatus.Complete, BulkStatus.Failed].includes(bulkBatch.status)
        )
        .map(bulkBatch => ({
          label:
            getUploadedFilename(bulkBatch, BulkResource.Collaborator) +
            ` (created ${toWSDateString(bulkBatch.createdAt)})`,
          name: bulkBatch.bulkCollaboratorBatchId
        }));

      config.push({
        title: "Bulk import batch",
        name: "bulkBatchId",
        type: "select",
        options: bulkBatchOptions
      });
    }

    return config;
  };
}

export function getInterceptFiltersChange(
  requirements: IRequirementDefinitionResponse[]
) {
  return function (prevValue: PayeeFilters, newValue: PayeeFilters) {
    if (
      newValue.eligibilityStatus ===
      PayeeFilterEligibilityStatus.SpecificRequirement
    ) {
      if (
        prevValue.requirementDefinitionId &&
        !newValue.requirementDefinitionId
      ) {
        return {
          ...newValue,
          eligibilityStatus: undefined,
          requirementStatus: undefined
        };
      } else if (
        !prevValue.requirementDefinitionId &&
        !newValue.requirementDefinitionId
      ) {
        return {
          ...newValue,
          requirementDefinitionId: requirements[0]?.requirementDefinitionId
        };
      }
    }
    return newValue;
  };
}
